import React, { useMemo, useState } from "react";
import { Box, Button, useTheme } from "@mui/material";
import { ListFilter } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PagesID } from "@utils/utilsConfigurations";
import { PageFilterField } from "@hooks/usePageFilters";
import { FiltersBarValue } from "@components/FiltersBarCst/types";
import { DynamicFilterOption } from "@components/DynamicFilterCst/types";
import FiltersAccordionCst from "./index";

interface FiltersAccordionToggleProps {
  pageId: PagesID | undefined;
  value: FiltersBarValue;
  onApply: (next: FiltersBarValue) => void;
  excludeKeys?: string[];
  filterVisibility?: (
    field: PageFilterField,
    value: FiltersBarValue,
  ) => boolean;
  dynamicOptions?: Record<string, DynamicFilterOption[]>;
  optionsKeyOverrides?: Record<string, string>;
}

const FiltersAccordionToggle: React.FC<FiltersAccordionToggleProps> = ({
  pageId,
  value,
  onApply,
  excludeKeys,
  filterVisibility,
  dynamicOptions,
  optionsKeyOverrides,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("catalog-v2");
  const [open, setOpen] = useState(false);

  const hasActiveFilters = useMemo(
    () =>
      Object.values(value || {}).some((val) => {
        if (val === undefined || val === null || val === "") return false;
        if (Array.isArray(val) && val.length === 0) return false;
        return true;
      }),
    [value],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex" }}>
        <Button
          variant="outlined"
          startIcon={<ListFilter size={16} />}
          onClick={() => setOpen((prev) => !prev)}
          sx={{
            height: "40px",
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            flexShrink: 0,
            transition: "none",
            borderColor:
              hasActiveFilters || open
                ? theme.palette.primary.main
                : theme.palette.divider,
            color:
              hasActiveFilters || open
                ? theme.palette.primary.main
                : theme.palette.text.primary,
            backgroundColor:
              hasActiveFilters || open
                ? theme.palette.action.hover
                : "transparent",
            "&:hover": {
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          {t("filters-button")}
        </Button>
      </Box>

      <FiltersAccordionCst
        open={open}
        onClose={() => setOpen(false)}
        pageId={pageId}
        value={value}
        onApply={onApply}
        excludeKeys={excludeKeys}
        filterVisibility={filterVisibility}
        dynamicOptions={dynamicOptions}
        optionsKeyOverrides={optionsKeyOverrides}
      />
    </Box>
  );
};

export default FiltersAccordionToggle;
